import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LabourMobilizationService, LabourDailyReport } from '../../shared/services/labour-mobilization.service';
import { ToastrMsgService } from '../../shared/services/shared-services/toastr.service';
import { ConfirmDeleteDialogComponent } from '../../shared/confirm-delete-dialog/confirm-delete-dialog.component';
import { ApiService } from '../../shared/services/api.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-daily-reports-list',
  templateUrl: './daily-reports-list.component.html',
  styleUrls: ['./daily-reports-list.component.css']
})
export class DailyReportsListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private destroy$ = new Subject<void>();

  displayedColumns: string[] = [
    'srNo',
    'report_date',
    'project_name',
    'contractor_name',
    'client_name',
    'shift_type',
    'total_manhours',
    'action'
  ];

  dataSource = new MatTableDataSource<LabourDailyReport>([]);
  searchKeyword = '';
  selectedProject: string = '';
  selectedShiftType: string = '';
  startDate: string = '';
  endDate: string = '';
  
  projects: any[] = [];
  loading = false;

  pagination = {
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10
  };

  shiftTypes = [
    { value: 'day', label: 'Day' },
    { value: 'night', label: 'Night' },
    { value: 'both', label: 'Both' }
  ];

  constructor(
    private labourService: LabourMobilizationService,
    private apiService: ApiService,
    private toastrMsgService: ToastrMsgService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadProjects();
    this.fetchData();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    
    if (this.paginator) {
      this.paginator.page.subscribe(() => {
        this.pagination.current_page = this.paginator.pageIndex + 1;
        this.pagination.items_per_page = this.paginator.pageSize;
        this.fetchData();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects(): void {
    this.apiService.getProjectCommonAll({ limit: 1000 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.projects = res.data || res || [];
        },
        error: (err) => {
          console.error('Error loading projects:', err);
        }
      });
  }

  fetchData(): void {
    this.loading = true;
    const params: any = {
      page: this.pagination.current_page,
      limit: this.pagination.items_per_page
    };

    if (this.searchKeyword) {
      params.search = this.searchKeyword;
    }
    if (this.selectedProject) {
      params.project_id = this.selectedProject;
    }
    if (this.selectedShiftType) {
      params.shift_type = this.selectedShiftType;
    }
    if (this.startDate) {
      params.start_date = this.startDate;
    }
    if (this.endDate) {
      params.end_date = this.endDate;
    }

    this.labourService.getDailyReports(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.data = response.data || [];
          if (response.pagination) {
            this.pagination = response.pagination;
          }
          this.loading = false;
        },
        error: (err) => {
          console.error('Error fetching daily reports:', err);
          this.toastrMsgService.error(err.error?.message || 'Failed to load daily reports');
          this.loading = false;
        }
      });
  }

  applyFilter(): void {
    this.pagination.current_page = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.fetchData();
  }

  onFilterChange(): void {
    this.applyFilter();
  }

  viewReport(report: LabourDailyReport): void {
    this.router.navigate(['/home/labour-mobilization/daily-reports', report.id]);
  }

  editReport(report: LabourDailyReport): void {
    this.router.navigate(['/home/labour-mobilization/daily-reports/edit', report.id]);
  }

  deleteReport(report: LabourDailyReport): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      data: { itemName: `Daily Report for ${report.project_name} on ${this.formatDate(report.report_date)}` }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && report.id) {
        this.labourService.deleteDailyReport(report.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              this.toastrMsgService.success('Daily report deleted successfully');
              this.fetchData();
            },
            error: (err) => {
              console.error('Error deleting report:', err);
              this.toastrMsgService.error(err.error?.message || 'Failed to delete daily report');
            }
          });
      }
    });
  }

  addNewReport(): void {
    this.router.navigate(['/home/labour-mobilization/daily-reports/add']);
  }

  formatDate(dateString: string): string {
    return this.labourService.formatDate(dateString);
  }

  getTotalManhours(report: LabourDailyReport): number {
    if (report.totals) {
      return report.totals.total;
    }
    if (report.details && report.details.length > 0) {
      return report.details.reduce((sum, detail) => sum + (detail.total_manhours || 0), 0);
    }
    return 0;
  }
}

